import React from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
    DateRangePicker,
    defaultStaticRanges,
    createStaticRanges,
} from "@atefbena/react-date-range";
import i18n from "i18next";
import frLocale from "date-fns/locale/fr";
import enUsLocale from "date-fns/locale/en-US";
import { useTranslation } from "react-i18next";
import { Button, Card } from "reactstrap";

import { getAdvancedSeeMorePeriod as getAdvancedPeriod } from "../../../helpers/general";
import { getAdvancedPeriodDate } from "../../../store/features/advancedDate";
import { changePeriod } from "../../../store/features/period";

import "@atefbena/react-date-range/dist/theme/default.css";
import "@atefbena/react-date-range/dist/styles.css";

type PropsType = {
    toggleOpenedPopover: () => void;
    isAdvancedReporting: boolean;
};


export default function DateRangePickerWrapper({
    toggleOpenedPopover,
    isAdvancedReporting,
}: PropsType) {
    moment.locale(i18n.language);
    const PERSONALIZED_RANGE_INDEX = isAdvancedReporting ? 4: 8;
    const { t } = useTranslation();
    const DATE_FORMAT = "DD/MM/YYYY";

    const dispatch = useDispatch();
    const { advancedDate, periodSlice } = useSelector((state: any) => state);
    const [state, setState] = React.useState([
        {
            startDate: new Date(
                Number(
                    getAdvancedPeriod(periodSlice.date, advancedDate).startDate
                ) * 1000
            ),
            endDate: new Date(
                Number(
                    getAdvancedPeriod(periodSlice.date, advancedDate).endDate
                ) * 1000
            ),
            key: "selection",
            label: t("The last 30 days"),
        },
    ]);

    const translatedDefaultStaticRanges = React.useMemo(
        () =>
            defaultStaticRanges
                .filter(
                    (range: any) =>
                        ![
                            // "Today",
                            "Last Year",
                            "This Week",
                            "This Month",
                            "The last 7 days",
                            "Today",
                            "Last Month",
                            "Yesterday",
                            "Last Week",
                        ].includes(range.label)
                )
                .map(({ label, range, isSelected }: any) => ({
                    label: t(label),
                    range,
                    isSelected,
                })),
        [t]
    );

    const staticRanges = React.useMemo(
        () =>
            createStaticRanges([
                ...translatedDefaultStaticRanges,
                ...(!isAdvancedReporting
                    ? [
                          {
                              label: t("The last 7 days"),
                              range: () => ({
                                  startDate: moment()
                                      .subtract(7, "days")
                                      .toDate(),
                                  endDate: moment()
                                      .subtract(1, "days")
                                      .toDate(),
                              }),
                          },
                          {
                              label: t("The last 30 days"),
                              range: () => ({
                                  startDate: moment()
                                      .subtract(30, "days")
                                      .toDate(),
                                  endDate: moment()
                                      .subtract(1, "days")
                                      .toDate(),
                              }),
                          },
                          {
                              label: t("Last Week"),
                              range: () => ({
                                  startDate: moment()
                                      .subtract(1, "week")
                                      .startOf("week")
                                      .toDate(),
                                  endDate: moment()
                                      .subtract(1, "week")
                                      .endOf("week")
                                      .toDate(),
                              }),
                          },
                          {
                              label: t("Last Month"),
                              range: () => ({
                                  startDate: moment()
                                      .subtract(1, "month")
                                      .startOf("month")
                                      .toDate(),
                                  endDate: moment()
                                      .subtract(1, "month")
                                      .endOf("month")
                                      .toDate(),
                              }),
                          },
                      ]
                    : []),
                {
                    label: t("Today"),
                    range: () => ({
                        startDate: moment().startOf("day").toDate(),
                        endDate: moment().endOf("day").toDate(),
                    }),
                },

                {
                    label: t("This Week"),
                    range: () => ({
                        startDate: moment().startOf("week").toDate(),
                        endDate: moment().endOf("week").toDate(),
                    }),
                },
                {
                    label: t("This Month"),
                    range: () => ({
                        startDate: moment().startOf("month").toDate(),
                        endDate: moment().endOf("month").toDate(),
                    }),
                },
                {
                    label: t("This Year"),
                    range: () => ({
                        startDate: moment().startOf("year").toDate(),
                        endDate: moment().endOf("year").toDate(),
                    }),
                },
                {
                    label: t("Personalized"),
                    range: () => ({
                        startDate:
                            advancedDate.startDate !== ""
                                ? new Date(
                                      Number(advancedDate.startDate) * 1000
                                  )
                                : moment().subtract(1, "days").toDate(),
                        endDate:
                            advancedDate.endDate !== ""
                                ? new Date(Number(advancedDate.endDate) * 1000)
                                : new Date(),
                    }),
                },
            ]),
        [translatedDefaultStaticRanges, t, advancedDate, isAdvancedReporting]
    );

    const handleOnChangeEvent = React.useCallback((item: any) => {
        setState([item.selection]);
    }, []);

    const handleValidateButtonOnClickEvent = React.useCallback(() => {
        const selectedRangeIndexInStaticRange = staticRanges.findIndex(
            (range: any) => {
                return (
                    moment(range.range().startDate).format(DATE_FORMAT) ===
                        moment(state[0].startDate).format(DATE_FORMAT) &&
                    moment(range.range().endDate).format(DATE_FORMAT) ===
                        moment(state[0].endDate).format(DATE_FORMAT)
                );
            }
        );

        if (
            selectedRangeIndexInStaticRange === -1 ||
            selectedRangeIndexInStaticRange === PERSONALIZED_RANGE_INDEX
        ) {
            dispatch(
                getAdvancedPeriodDate({
                    startDate: String(moment(state[0].startDate).unix()),
                    endDate: String(moment(state[0].endDate).unix()),
                })
            );
            dispatch(changePeriod(t("Personalized")));
        } else {
            dispatch(
                getAdvancedPeriodDate({
                    startDate: "",
                    endDate: "",
                })
            );
            dispatch(
                changePeriod(
                    staticRanges[selectedRangeIndexInStaticRange]?.label
                )
            );
        }

        toggleOpenedPopover();
    }, [state, staticRanges, dispatch, toggleOpenedPopover, t]);

    return (
        <Card
            style={{ boxShadow: "0px 11px 14px 7px rgba(0, 0, 0, 0.11)" }}
            className="pb-1"
        >
            <DateRangePicker
                onChange={handleOnChangeEvent}
                months={1}
                ranges={state}
                direction="horizontal"
                maxDate={new Date()}
                weekStartsOn={1}
                locale={i18n.language === "fr" ? frLocale : enUsLocale}
                dateDisplayFormat="dd MMM yyyy"
                staticRanges={staticRanges}
                className="input-ranges-display-none__clz"
            />
            <div className="d-flex justify-content-end mb-2">
                <Button
                    type="button"
                    color="dark"
                    className="ml-3"
                    outline
                    onClick={toggleOpenedPopover}
                >
                    {t("Cancel")}
                </Button>
                <Button
                    type="button"
                    color="dark"
                    onClick={handleValidateButtonOnClickEvent}
                    className="mx-3"
                >
                    {t("Validate")}
                </Button>
            </div>
        </Card>
    );
}
